import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import withRouter from '../lib/withRouter';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import './CallTasks.scss';

class CallTasks extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.toast = React.createRef();

        this.state = {
            loading: false,
            tasks: [],
            stats: {
                total: 0,
                completed: 0,
                pending: 0,
                completionRate: 0,
                byRole: {}
            },
            
            // Outcome Dialog
            outcomeDialog: false,
            selectedTask: null,
            
            // Outcome Form
            outcomeData: {
                outcome: '',
                promiseDate: null,
                remarks: '',
                callDuration: null,
                callStartTime: new Date() // Auto-capture when dialog opens
            },
            
            // Filter
            assignedTo: null
        };

        // Call outcome options
        this.outcomeOptions = [
            { label: 'Connected - Payment Promised', value: 'PROMISED' },
            { label: 'Connected - Already Paid', value: 'PAID' },
            { label: 'Connected - Need Time', value: 'CONNECTED' },
            { label: 'No Answer', value: 'NO_ANSWER' },
            { label: 'Busy', value: 'BUSY' },
            { label: 'Wrong Number', value: 'WRONG_NUMBER' },
            { label: 'Refused to Talk', value: 'REFUSED' },
            { label: 'Callback Requested', value: 'CALLBACK_REQUESTED' }
        ];

        this.assignedToOptions = [
            { label: 'All Roles', value: null },
            { label: 'Principal', value: 'PRINCIPAL' },
            { label: 'Coordinator', value: 'COORDINATOR' },
            { label: 'Accounts', value: 'ACCOUNTS' },
            { label: 'Teacher/HRT', value: 'TEACHER' }
        ];
    }

    componentDidMount() {
        this.fetchTodaysTasks();
        // Auto-refresh every 5 minutes
        this.refreshInterval = setInterval(() => this.fetchTodaysTasks(true), 5 * 60 * 1000);
    }

    componentWillUnmount() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    }

    fetchTodaysTasks = async (isBackground = false) => {
        if (!isBackground) {
            this.setState({ loading: true });
        }

        try {
            let url = `${baseUrlAdmin}/fee-decision/actions/mobile/today`;
            
            if (this.state.assignedTo) {
                url += `?assignedTo=${this.state.assignedTo}`;
            }

            const response = await this.service.get(url, true);

            if (response?.status && response?.res?.success) {
                this.setState({
                    tasks: response.res.data.tasks || [],
                    stats: response.res.data.stats || {
                        total: 0,
                        completed: 0,
                        pending: 0,
                        completionRate: 0
                    }
                });
            }
        } catch (error) {
            console.error('Error fetching tasks:', error);
            this.showToast('error', 'Error', 'Failed to fetch today\'s tasks');
        } finally {
            if (!isBackground) {
                this.setState({ loading: false });
            }
        }
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({ severity, summary, detail, life: 3000 });
    }

    openOutcomeDialog = (task) => {
        this.setState({
            outcomeDialog: true,
            selectedTask: task,
            outcomeData: {
                outcome: '',
                promiseDate: null,
                remarks: '',
                // Do not auto-populate time unless we have a real dialer
                callDuration: null,
                callStartTime: null 
            }
        });
    }

    logOutcome = async () => {
        const { selectedTask, outcomeData } = this.state;

        if (!outcomeData.outcome) {
            this.showToast('warn', 'Required', 'Please select an outcome');
            return;
        }

        // If promised, promiseDate is required
        if (outcomeData.outcome === 'PROMISED' && !outcomeData.promiseDate) {
            this.showToast('warn', 'Required', 'Please select promise date');
            return;
        }
        
        // Only calculate duration if start time was explicitly tracked (e.g. by a future 'Start Call' button)
        let durationInSeconds = null;
        if (outcomeData.callStartTime) {
             const callEndTime = new Date();
             durationInSeconds = Math.floor((callEndTime - outcomeData.callStartTime) / 1000);
        }

        this.setState({ loading: true });

        try {
            const url = `${baseUrlAdmin}/fee-decision/actions/mobile/log-outcome`;
            const payload = {
                actionId: selectedTask._id,
                outcome: outcomeData.outcome,
                promiseDate: outcomeData.promiseDate,
                remarks: outcomeData.remarks
            };
            
            // Only send call metrics if they exist
            // Priority: Calculated Duration (if auto-tracking) > Manual Entry
            if (durationInSeconds !== null) {
                payload.callDuration = durationInSeconds;
                payload.callStartTime = outcomeData.callStartTime;
            } else if (outcomeData.callDuration) {
                payload.callDuration = parseInt(outcomeData.callDuration, 10);
            }

            const response = await this.service.post(url, payload, true);

            if (response?.status && response?.res?.success) {
                this.showToast('success', 'Success', `Call outcome logged`);
                this.setState({ outcomeDialog: false });
                this.fetchTodaysTasks(); // Refresh list
            } else {
                throw new Error(response?.res?.message || 'Failed to log outcome');
            }
        } catch (error) {
            console.error('Error logging outcome:', error);
            this.showToast('error', 'Error', error.message || 'Failed to log outcome');
        } finally {
            this.setState({ loading: false });
        }
    }

    // --- Column Templates ---

    studentTemplate = (rowData) => {
        return (
            <div className="student-info">
                <div className="student-name">{rowData.studentName || 'Unknown'}</div>
                <div className="student-details">
                    <span className="enrollment-no">{rowData.enrollmentNo || 'N/A'}</span>
                    {rowData.className && <span className="class-name"> | {rowData.className}</span>}
                </div>
            </div>
        );
    }

    riskTemplate = (rowData) => {
        const band = rowData.riskBand?.toUpperCase();
        let severity = null;
        let style = null;

        switch (band) {
            case 'CRITICAL': style = { backgroundColor: '#FFA6BC', color: '#BF0031' }; break;
            case 'HIGH': style = { backgroundColor: '#FFEDD5', color: '#C25400' }; break; // Explicit Dark Orange
            case 'MEDIUM': style = { backgroundColor: '#FEF9C3', color: '#854D0E' }; break; // Explicit Yellow
            case 'LOW': style = { backgroundColor: '#DCFCE7', color: '#268900' }; break;
            default: style = { backgroundColor: '#E6E8EA', color: '#4B5563' };
        }
        
        return (
            <div className="risk-info">
                <Tag value={rowData.riskBand} severity={severity} style={style} />
                <div className="risk-score mt-1">{rowData.riskScore}/100</div>
            </div>
        );
    }

    contactTemplate = (rowData) => {
        return (
            <div className="contact-info">
                {rowData.parentName && <div className="parent-name">{rowData.parentName}</div>}
                {rowData.phoneNumber && (
                    <a href={`tel:${rowData.phoneNumber}`} className="phone-link">
                        <i className="pi pi-phone" /> {rowData.phoneNumber}
                    </a>
                )}
            </div>
        );
    }

    actionTemplate = (rowData) => {
        return (
            <div className="action-buttons">
                <Authorizer permId={PERMISSIONS.FDE_MARK_TASK_COMPLETE}>
                    <Button 
                        label="Log Call" 
                        icon="pi pi-phone" 
                        className="p-button-sm p-button-success"
                        onClick={() => this.openOutcomeDialog(rowData)}
                    />
                </Authorizer>
            </div>
        );
    }

    render() {
        const { loading, tasks, stats, outcomeDialog, selectedTask, outcomeData, assignedTo } = this.state;

        if (loading) {
            return <LoadingComponent />;
        }

        return (
            <div className="call-tasks-container">
                <Toast ref={this.toast} position="bottom-right" />

                <Card className="stats-card">
                    <div className="stats-header">
                        <h2>Today's Call Tasks</h2>
                        <div className="header-actions">
                            <Button 
                                label="View All Logs"
                                icon="pi pi-list"
                                className="p-button-text"
                                onClick={() => this.props.navigate('/financeNew/CallOutcomesLog')}
                                tooltip="View detailed call history"
                            />
                            <Button 
                                icon="pi pi-refresh" 
                                className="p-button-text p-button-rounded"
                                onClick={this.fetchTodaysTasks}
                                tooltip="Refresh"
                                tooltipOptions={{ position: 'bottom' }}
                            />
                        </div>
                    </div>
                    
                    <div className="stats-content">
                        <div className="stat-item">
                            <span className="stat-label">Total Tasks</span>
                            <span className="stat-value">{stats.total}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Completed</span>
                            <span className="stat-value success">{stats.completed}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Pending</span>
                            <span className="stat-value pending">{stats.pending}</span>
                        </div>
                    </div>

                    <div className="progress-section">
                        <div className="progress-label">
                            <span>Completion Rate</span>
                            <span className="progress-percentage">{stats.completionRate}%</span>
                        </div>
                        <ProgressBar value={stats.completionRate} showValue={false} />
                    </div>
                    
                    {/* Role-based Breakdown */}
                    {stats.byRole && Object.keys(stats.byRole).length > 0 && (
                        <div className="role-breakdown">
                            <h4>By Role</h4>
                            <div className="role-stats">
                                {Object.entries(stats.byRole).map(([role, data]) => (
                                    <div key={role} className="role-stat-item">
                                        <span className="role-name">
                                            {role.replace('_', ' ')}: 
                                        </span>
                                        <span className="role-progress">
                                            {data.completed}/{data.total}
                                        </span>
                                        <Tag 
                                            value={`${Math.round((data.completed/data.total)*100)}%`}
                                            severity={data.completed === data.total ? 'success' : 'warning'}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Card>

                <Card className="tasks-card">
                    <div className="card-header">
                        <h3>Call List (Priority Order)</h3>
                        <Dropdown 
                            value={assignedTo}
                            options={this.assignedToOptions}
                            onChange={(e) => this.setState({ assignedTo: e.value }, this.fetchTodaysTasks)}
                            placeholder="Filter by Role"
                        />
                    </div>

                    <DataTable 
                        value={tasks}
                        loading={loading}
                        emptyMessage="No calls scheduled for today"
                        className="tasks-table"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                    >
                        <Column 
                            field="studentName" 
                            header="Student" 
                            body={this.studentTemplate}
                        />
                        <Column 
                            field="riskBand" 
                            header="Risk" 
                            body={this.riskTemplate}
                            style={{ width: '120px' }}
                        />
                        <Column 
                            header="Contact" 
                            body={this.contactTemplate}
                        />
                        <Column 
                            field="scriptTone" 
                            header="Tone"
                            body={(rowData) => {
                                let color = 'bg-blue-100 text-blue-800';
                                if (rowData.scriptTone === 'URGENT') color = 'bg-red-100 text-red-800';
                                else if (rowData.scriptTone === 'FIRM') color = 'bg-orange-100 text-orange-800';
                                
                                return (
                                    <span className={`term-badge ${color} border-round px-2 py-1 text-sm font-semibold`}>
                                        {rowData.scriptTone}
                                    </span>
                                );
                            }}
                            style={{ width: '100px' }}
                        />
                        <Column 
                            header="Actions" 
                            body={this.actionTemplate}
                            style={{ width: '150px' }}
                        />
                    </DataTable>
                </Card>

                {/* Outcome Dialog */}
                <Dialog
                    visible={outcomeDialog}
                    onHide={() => this.setState({ outcomeDialog: false })}
                    header={`Log Call Outcome - ${selectedTask?.studentName || ''}`}
                    style={{ width: '500px' }}
                    className="outcome-dialog"
                >
                    <div className="p-fluid">
                        <div className="p-field">
                            <label>Call Outcome *</label>
                            <Dropdown
                                value={outcomeData.outcome}
                                options={this.outcomeOptions}
                                onChange={(e) => this.setState({
                                    outcomeData: { ...outcomeData, outcome: e.value }
                                })}
                                placeholder="Select outcome"
                            />
                        </div>

                        {outcomeData.outcome === 'PROMISED' && (
                            <div className="p-field">
                                <label>Promise Date *</label>
                                <Calendar
                                    value={outcomeData.promiseDate}
                                    onChange={(e) => this.setState({
                                        outcomeData: { ...outcomeData, promiseDate: e.value }
                                    })}
                                    minDate={new Date()}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                />
                            </div>
                        )}

                        <div className="p-field">
                            <label>Call Duration (seconds)</label>
                            <InputTextarea
                                type="number"
                                value={outcomeData.callDuration}
                                onChange={(e) => this.setState({
                                    outcomeData: { ...outcomeData, callDuration: e.target.value }
                                })}
                                placeholder="Optional"
                            />
                        </div>

                        <div className="p-field">
                            <label>Remarks</label>
                            <InputTextarea
                                value={outcomeData.remarks}
                                onChange={(e) => this.setState({
                                    outcomeData: { ...outcomeData, remarks: e.target.value }
                                })}
                                rows={3}
                                placeholder="Optional notes about the call"
                            />
                        </div>

                        <div className="dialog-footer">
                            <Button 
                                label="Cancel" 
                                icon="pi pi-times" 
                                className="p-button-text"
                                onClick={() => this.setState({ outcomeDialog: false })}
                            />
                            <Button 
                                label="Save Outcome" 
                                icon="pi pi-check" 
                                onClick={this.logOutcome}
                                loading={loading}
                            />
                        </div>
                    </div>
                </Dialog>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.user
});

export default connect(mapStateToProps)(withRouter(CallTasks));
